/**
 * Status card showing WhatsApp integration health and information
 */
import { useState, useEffect, useCallback } from 'react';
import { whatsappApi } from '../../services/api';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Tooltip,
  Box,
  Alert,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircle2,
  Cancel as XCircle,
  Warning as AlertCircle,
  AccessTime as Clock,
  Phone,
  Settings,
  Refresh as RefreshCw,
  TrendingUp,
  Warning as AlertTriangle,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useNotification } from '../../contexts/NotificationContext';
import { WhatsAppDetailsDialog } from './WhatsAppDetailsDialog';

export interface WhatsAppStatusData {
  has_credentials: boolean;
  status: string;
  smb_name?: string;
  phone_number?: string;
  display_phone_number?: string;
  business_name?: string;
  business_account_id?: string;
  phone_number_id?: string;
  app_id?: string;
  is_verified: boolean;
  is_healthy: boolean;
  verified_at?: string;
  last_health_check?: string;
  health_check_status?: string;
  health_check_message?: string;
  quality_score?: string;
  messaging_limit?: string;
  api_calls_today: string;
  api_calls_this_month: string;
  last_error_at?: string;
  last_error_message?: string;
  consecutive_failures: string;
  channel_id?: string | null;
  channel_is_active?: boolean | null;
  active_smb_id?: string | null;
  active_smb_name?: string | null;
}

interface WhatsAppStatusCardProps {
  smbId?: string | null;
  onConfigureCredentials: () => void;
}

export function WhatsAppStatusCard({
  smbId,
  onConfigureCredentials
}: WhatsAppStatusCardProps) {
  const [statusData, setStatusData] = useState<WhatsAppStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activating, setActivating] = useState(false);
  const { showNotification } = useNotification();

  const resolvedSmbId = smbId?.trim() || null;

  const fetchStatus = useCallback(async () => {
    if (!resolvedSmbId) {
      setStatusData(null);
      setLoading(false);
      return;
    }
    try {
      const data = await whatsappApi.getStatus(resolvedSmbId);
      setStatusData(data);
    } catch (error) {
      console.error('Error fetching WhatsApp status:', error);
      showNotification('Failed to load WhatsApp status', 'error');
      setStatusData(null);
    } finally {
      setLoading(false);
    }
  }, [resolvedSmbId, showNotification]);

  useEffect(() => {
    if (resolvedSmbId) {
      setLoading(true);
    }
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ smbId?: string }>;
      if (!customEvent.detail?.smbId || customEvent.detail.smbId === resolvedSmbId) {
        fetchStatus();
      }
    };
    window.addEventListener('whatsapp-credentials-updated', handler);
    return () => {
      window.removeEventListener('whatsapp-credentials-updated', handler);
    };
  }, [fetchStatus, resolvedSmbId]);

  const refreshStatus = async () => {
    setRefreshing(true);
    await fetchStatus();
    setRefreshing(false);
  };

  const activateForThisSMB = async () => {
    if (!resolvedSmbId) {
      showNotification('Select a business before activating WhatsApp routing.', 'warning');
      return;
    }

    setActivating(true);
    try {
      await whatsappApi.activate(resolvedSmbId, statusData?.phone_number_id);

      showNotification('This SMB now handles replies for the shared WhatsApp number.', 'success');
      await fetchStatus();
      window.dispatchEvent(new CustomEvent('whatsapp-credentials-updated', { detail: { smbId: resolvedSmbId } }));
    } catch (error) {
      console.error('Failed to activate WhatsApp credentials:', error);
      showNotification(error instanceof Error ? error.message : 'Failed to activate WhatsApp routing.', 'error');
    } finally {
      setActivating(false);
    }
  };

  const openDetails = () => setDetailsOpen(true);
  const closeDetails = () => setDetailsOpen(false);

  const runHealthCheck = async () => {
    setRefreshing(true);
    try {
      if (!resolvedSmbId) {
        showNotification('Select a business to run a health check.', 'warning');
        return;
      }

      const data = await whatsappApi.verify(resolvedSmbId);

      if (data && typeof data === 'object') {
        setStatusData(prev => prev ? {
          ...prev,
          status: data.credential_status || prev.status,
          health_check_status: data.health_check_status ?? prev.health_check_status,
          last_health_check: data.last_checked || prev.last_health_check,
          is_healthy: typeof data.is_healthy === 'boolean' ? data.is_healthy : prev.is_healthy,
          business_name: data.business_profile?.name || prev.business_name,
          quality_score: data.account_quality?.quality_score || prev.quality_score,
        } : prev);
      }

      const healthSuccessful = Boolean(data?.is_healthy) || data?.health_check_status === 'healthy';

      if (healthSuccessful) {
        showNotification('Health check completed', 'success');
      } else {
        showNotification(data?.message || 'Health check failed', 'error');
      }

      await fetchStatus();
    } catch (error) {
      showNotification('Failed to run health check', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  const getStatusIcon = () => {
    if (!statusData?.has_credentials) {
      return <Settings sx={{ color: 'text.secondary' }} />;
    }

    switch (statusData.status) {
      case 'verified':
        return <CheckCircle2 sx={{ color: 'success.main' }} />;
      case 'pending':
        return <Clock sx={{ color: 'warning.main' }} />;
      case 'invalid':
      case 'expired':
        return <XCircle sx={{ color: 'error.main' }} />;
      case 'rate_limited':
        return <AlertTriangle sx={{ color: 'warning.dark' }} />;
      default:
        return <AlertCircle sx={{ color: 'text.secondary' }} />;
    }
  };

  const getStatusBadge = () => {
    if (!statusData?.has_credentials) {
      return <Chip label="Not Configured" variant="outlined" />;
    }

    const statusConfig = {
      verified: { label: 'Verified', color: 'success' as const },
      pending: { label: 'Pending', color: 'warning' as const },
      invalid: { label: 'Invalid', color: 'error' as const },
      expired: { label: 'Expired', color: 'error' as const },
      rate_limited: { label: 'Rate Limited', color: 'warning' as const },
      suspended: { label: 'Suspended', color: 'error' as const }
    };

    const config = statusConfig[statusData.status as keyof typeof statusConfig];
    return <Chip label={config?.label || statusData.status} color={config?.color || 'default'} variant="outlined" />;
  };

  const getApiUsagePercentage = () => {
    if (!statusData?.messaging_limit) return 0;
    const limit = parseInt(statusData.messaging_limit);
    const used = parseInt(statusData.api_calls_today);
    return Math.min((used / limit) * 100, 100);
  };

  const getHealthColor = () => {
    if (!statusData?.health_check_status) return 'text.secondary';
    if (statusData.health_check_status === 'healthy') return 'success.main';
    if (statusData.health_check_status === 'unknown') return 'text.secondary';
    return 'error.main';
  };

  const getHealthLabel = () => {
    if (!statusData?.health_check_status) return 'Unknown';
    const label = statusData.health_check_status;
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  if (!resolvedSmbId) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6">WhatsApp Integration</Typography>
          <Typography variant="body2" color="text.secondary">
            Select a business to view WhatsApp integration details.
          </Typography>
          <Button
            variant="outlined"
            onClick={onConfigureCredentials}
            size="small"
            sx={{ mt: 2 }}
            startIcon={<Settings />}
          >
            Configure WhatsApp
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (!statusData?.has_credentials) {
    return (
      <Card>
        <CardHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getStatusIcon()}
              <Typography variant="h6">WhatsApp Integration</Typography>
            </Box>
            {getStatusBadge()}
          </Box>
          <Typography variant="body2" color="text.secondary">
            WhatsApp Business API credentials are not configured yet.
          </Typography>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={onConfigureCredentials} 
            variant="contained"
            fullWidth
            startIcon={<Settings />}
          >
            Configure WhatsApp
          </Button>
        </CardContent>
      </Card>
    );
  }

  const isCurrentSMBActive = statusData?.channel_is_active ?? (statusData?.active_smb_id ? statusData.active_smb_id === resolvedSmbId : false);

  return (
    <>
      <Card>
      <CardHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon()}
            <Typography variant="h6">WhatsApp Integration</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusBadge()}
            <Tooltip title="Refresh status">
              <IconButton
                onClick={refreshStatus}
                disabled={refreshing}
                size="small"
              >
                <RefreshCw sx={{ transform: refreshing ? 'rotate(360deg)' : 'none', transition: 'transform 1s' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {(statusData.business_name || statusData.smb_name) && (
          <Typography variant="body2" color="text.secondary">
            {statusData.business_name || statusData.smb_name}
          </Typography>
        )}
      </CardHeader>

      <CardContent sx={{ pt: 0 }}>
        {/* Phone Number */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Phone sx={{ color: 'text.secondary', fontSize: '1rem' }} />
          <Typography variant="body2" fontWeight="medium">
            {statusData.display_phone_number || statusData.phone_number}
          </Typography>
        </Box>

        {!isCurrentSMBActive && statusData?.active_smb_id && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {`${statusData.active_smb_name || 'Another business'} currently handles replies for this WhatsApp number. Activate ${statusData.smb_name || 'this SMB'} to switch routing.`}
          </Alert>
        )}

        {/* Health Status */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Health Status</Typography>
            <Typography 
              variant="body2" 
              fontWeight="medium"
              sx={{ color: getHealthColor() }}
            >
              {getHealthLabel()}
            </Typography>
          </Box>
          
          {statusData.last_health_check && (
            <Typography variant="caption" color="text.secondary">
              Last checked {formatDistanceToNow(new Date(statusData.last_health_check))} ago
            </Typography>
          )}
        </Box>

        {/* API Usage */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUp sx={{ color: 'text.secondary', fontSize: '1rem' }} />
              <Typography variant="body2" color="text.secondary">Today's Usage</Typography>
            </Box>
            <Typography variant="body2" fontWeight="medium">
              {statusData.api_calls_today}
              {statusData.messaging_limit && ` / ${statusData.messaging_limit}`}
            </Typography>
          </Box>
          
          {statusData.messaging_limit && (
            <LinearProgress 
              variant="determinate" 
              value={getApiUsagePercentage()} 
              sx={{ height: 4, borderRadius: 2 }}
            />
          )}
        </Box>

        {/* Quality Score */}
        {statusData.quality_score && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">Quality Score</Typography>
            <Chip 
              label={statusData.quality_score}
              color={statusData.quality_score === 'GREEN' ? 'success' : 
                     statusData.quality_score === 'YELLOW' ? 'warning' : 'error'}
              variant="outlined"
              size="small"
            />
          </Box>
        )}

        {/* Error Information */}
        {statusData.last_error_message && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                Last Error
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {statusData.last_error_message}
              </Typography>
              {statusData.last_error_at && (
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                  {formatDistanceToNow(new Date(statusData.last_error_at))} ago
                </Typography>
              )}
            </Box>
          </Alert>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, pt: 1, flexWrap: 'wrap' }}>
          {statusData.has_credentials && !isCurrentSMBActive && (
            <Button
              onClick={activateForThisSMB}
              disabled={activating}
              size="small"
              variant="contained"
              color="secondary"
              startIcon={activating ? <CircularProgress size={16} /> : <CheckCircle2 />}
            >
              Set as Active SMB
            </Button>
          )}

          {statusData.has_credentials && (
            <Button 
              onClick={runHealthCheck} 
              disabled={refreshing}
              size="small"
              variant="contained"
              startIcon={refreshing ? <CircularProgress size={16} /> : undefined}
            >
              {statusData.status === 'verified' ? 'Re-Verify' : 'Verify Now'}
            </Button>
          )}

          <Button 
            variant="outlined" 
            onClick={onConfigureCredentials} 
            size="small"
            startIcon={<Settings />}
          >
            Settings
          </Button>
          
          {statusData?.has_credentials && (
            <Button 
              variant="outlined" 
              onClick={openDetails} 
              size="small"
            >
              View Details
            </Button>
          )}
        </Box>
      </CardContent>
      </Card>

      {statusData && (
        <WhatsAppDetailsDialog open={detailsOpen} onClose={closeDetails} status={statusData} />
      )}
    </>
  );
}
